import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs, { locale } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { IconButton, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { IResidentDFreeGraph } from '@/types/residents/dfreeGraph';
import { color, ThreeDotsAnimation } from '@/styles';
import { useChartResizeObserver } from '@/hooks/useChartResizeObserver';
import NotificationCover from '../notificationCover';
import DfreeGraphListItemHeader from './dfreeGraphListItemHeader';
import { NotificationCoverConfig } from '@/types/notificationCover';
import { createActivityStatusHeatmapBar } from '@/utils/activityStatusUtils';
import { ActivityTrendColorGuideTooltip } from './activityTrendColorGuideTooltip';
import { UrinatedNotificationHintTooltip } from './urinatedNotificationHintTooltip';
import { BigBladderNotificationHintTooltip } from './bigBladderNotificationHintTooltip';

require('highcharts/modules/accessibility')(Highcharts);
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(duration);

interface Props {
  resident: IResidentDFreeGraph;
  dateRange: { start: string; end: string };
  fetch: () => void;
  openDialog: (resident: IResidentDFreeGraph) => void;
  resetNotification: (residentId: number, coverTypes?: string[]) => void;
  showRawGraph: boolean;
}

const ResidentItemContainer = styled.div<{ resident: IResidentDFreeGraph }>`
  position: relative;
  background: ${color.white};
  box-sizing: border-box;
  border-radius: 8px;
  margin-bottom: 16px;
  .highcharts-scatter-series {
    .highcharts-point {
      opacity: 1;
    }
  }
`;

const ResidentContent = css`
  padding: 16px 18px 10px 24px;
  display: flex;
  border-top: 1px solid ${color.border_gray};
`;

const LeftContainer = css`
  position: relative;
  width: 195px;
  height: fit-content;
`;

const UrineThreshold = (bottom: number) => css`
  position: absolute;
  top: ${`calc(50% + ${bottom}px)`};
  transform: translateY(-50%);
`;

const UrineLevelMeter = css`
  position: relative;
  left: 21px;
  width: 130px;
  height: 130px;
`;

const BDFreeDisconnectedText = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  font-size: 9px;
  line-height: 1;
  font-weight: bold;
  color: ${color.gray};
  text-align: center;
`;

const DFreeDisconnectedText = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  font-size: 13px;
  line-height: 1;
  font-weight: bold;
  color: ${color.gray};
  text-align: center;
`;

const GasLevelMeter = (isFront: boolean) => css`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 60px;
  height: 60px;
  z-index: ${isFront ? 10 : 0};

  > img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 60px;
    max-height: 60px;
  }
`;

const RightContainer = css`
  padding-left: 16px;
  width: 265px;
  display: flex;
  flex-direction: column;
  row-gap: 18px;
  border-left: 1px dotted ${color.border_gray};
`;

const GasDaysOfNoDefecation = css`
  margin: 0 auto;
  display: flex;
  column-gap: 17.5px;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  color: ${color.dark_gray};
`;

const GasDaysOfNoDefecationValue = (textColor: string, bgColor: string) => css`
  padding: 3px 8px;
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
  color: ${textColor};
  background-color: ${bgColor};
  border-radius: 3px;
`;

const CareGuideline = css`
  border: 1px solid ${color.border_gray};
  border-radius: 7px;
`;

const GasPrediction = css`
  padding: 10px 8px 8.5px;
  border-bottom: 1px dotted ${color.thin_gray};
`;

const GasPredictionInner = css`
  margin: 0 auto;
  min-width: 177px;
  width: fit-content;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${color.dark_gray};
`;

const GasPredictionTitle = css`
  max-width: 60%;
`;

const GasPreditionTime = (textColor: string) => css`
  flex: 1;
  padding: 0 6px 0 9px;
  font-size: 14px;
  font-weight: bold;
  color: ${textColor};
  text-align: center;
`;

const LaxativeTime = css`
  padding: 7.5px 15px 17px 8px;
  display: flex;
  align-items: center;
  column-gap: 6px;
  font-size: 12px;
  color: ${color.dark_gray};
`;

const LaxativeTimeMeter = css`
  position: relative;
  min-width: 138px;
  height: 23px;
  background-color: ${color.table_gray};
`;

const LaxativeTimeMeterGasElapsedTime = (minutes: number) => css`
  position: absolute;
  left: 0;
  max-width: 100%;
  width: ${`${(minutes / 1440) * 100}%`};
  height: 100%;

  // バー
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 100%;
    background-color: ${color.light_orange};
  }
`;

const LaxativeTimeMeterGuideline = (hours: number) => css`
  position: absolute;
  left: ${`${(hours / 24) * 100}%`};
  height: 100%;

  // バー
  &::before {
    content: ${hours != 24 ? '""' : 'none'};
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 100%;
    border-right: 1px dotted ${color.thin_gray};
  }

  // テキスト
  > span {
    position: absolute;
    top: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%);
    font-size: 9px;
    color: ${color.gray};
  }
`;

const LaxativeTimeMeterGasPreditionTime = (minutes: number) => css`
  position: absolute;
  left: ${`${(minutes / 1440) * 100}%`};
  height: 100%;

  // バー
  &::before {
    content: ${minutes <= 1440 ? '""' : 'none'};
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 100%;
    border-right: 2px solid ${color.notification_orange};
  }
`;

const GraphContainer = css`
  flex: 1;
  min-width: 0;
`;

const DotAnimeContainer = css`
  width: 30px;
  display: flex;
  justify-content: center;
`;

const Icon = css`
  flex: 0 0 40px;
  width: 40px;
  height: auto;
`;

export const DfreeGraphListItem = React.memo(
  ({ resident, dateRange, fetch, openDialog, resetNotification, showRawGraph }: Props) => {
    const {
      bigBladderNotificationSetting,
      sensorReactions,
      rawSensorReactions,
      bladderThresholdPercentage,
      sensorNotifications,
      careRecordPlots,
      activityStatusAnalysis,
    } = resident;
    const chartRef = React.useRef<HighchartsReact.RefObject>(null);
    useChartResizeObserver(chartRef);
    const { t, i18n } = useTranslation();
    const isJa = i18n.language.startsWith('ja');
    locale(i18n.language);

    const handleClickRecord = React.useCallback(() => {
      openDialog(resident);
    }, []);

    const handleNotificationReset = React.useCallback((coverType?: string) => {
      resetNotification(resident.id, coverType ? [coverType] : undefined);
    }, []);

    const notificationCoverConfig: NotificationCoverConfig = {
      bigBladderNotification: {
        colorCode: color.notification_yellow,
        icon: <img src='/images/notification/large_icon/large_bigbladder.svg' alt='そろそろ通知' css={Icon} />,
        message: t('exceededAlmostThreshold', 'そろそろラインを超えました'),
        hint: isJa ? <BigBladderNotificationHintTooltip /> : null,
        buttonText: t('recordButton', '記録'),
        buttonAction: handleClickRecord,
      },
      urinatedNotification: {
        colorCode: color.notification_pink,
        icon: <img src='/images/notification/large_icon/large_urinated.svg' alt='出たかも通知' css={Icon} />,
        message:
          t('check(urine)', 'でたかもしれません') +
          (resident.latestUrineVolume ? ' ' + resident.latestUrineVolume + ' ml' : ''),
        hint: isJa ? <UrinatedNotificationHintTooltip /> : null,
        buttonText: t('recordButton', '記録'),
        buttonAction: handleClickRecord,
      },
      gotUpNotification: {
        colorCode: color.notification_red,
        icon: <img src='/images/notification/large_icon/large_gotUp.svg' alt='起き上がり通知' css={Icon} />,
        message: t('detectedGettingUp', '起き上がりを検知しました'),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('gotUp'),
      },
      gasPoopedNotification: {
        colorCode: color.notification_orange,
        icon: <img src='/images/notification/large_icon/large_pooped.svg' alt='出たかも通知（便）' css={Icon} />,
        message: t('odorDetected(stool)', '臭いを検知しました(便)'),
        buttonText: t('recordButton', '記録'),
        buttonAction: handleClickRecord,
      },
      gasPredictionNotification: {
        colorCode: color.notification_green,
        icon: <img src='/images/notification/large_icon/large_prediction.svg' alt='排便予測通知' css={Icon} />,
        message: t('predictedDefecationTime', '排便予測時間です'),
        buttonText: t('recordButton', '記録'),
        buttonAction: handleClickRecord,
      },
      urineResetNotification: {
        colorCode: color.notification_navy,
        icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
        message: t('measurementResumed', '測定が再開されました(尿)'),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('urineReset'),
      },
      sensorErrorNotification: {
        colorCode: color.notification_navy,
        icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
        message: t('SensorIsRemoved(urine)', 'センサが外れています(尿)'),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('sensorError'),
      },
      zeroResetNotification: {
        colorCode: color.notification_navy,
        icon: (
          <img
            src='/images/notification/large_icon/large_alert_without_border.svg'
            alt='ジェルの不足や浮きをご確認ください'
            css={Icon}
          />
        ),
        message: t(
          'checkToSeeIfMoreGelIsNeededOrMakeSureTheDeviceIsAttachedToSkin',
          'ジェルの不足や浮きをご確認ください',
        ),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('zeroReset'),
      },
      upsideDownNotification: {
        colorCode: color.notification_navy,
        icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
        message: t('TheSensorIsUpsideDown(urine)', 'センサの向きが上下逆です(尿)'),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('upsideDown'),
      },
      moveUpperAdviceNotification: {
        colorCode: color.notification_navy,
        icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
        message: t('raiseTheSensorPosition', 'センサの位置を上げてください'),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('moveUpperAdvice'),
      },
      moveLowerAdviceNotification: {
        colorCode: color.notification_navy,
        icon: <img src='/images/notification/large_icon/large_alert_without_border.svg' alt='アラート' css={Icon} />,
        message: t('lowerTheSensorPosition', 'センサの位置を下げてください'),
        buttonText: 'OK',
        buttonAction: () => handleNotificationReset('moveLowerAdvice'),
      },
    };

    const getYGrids = React.useCallback(() => {
      let grids: Highcharts.YAxisPlotLinesOptions[] = [];

      for (let i = 0; i < 10; i++) {
        grids = [
          ...grids,
          {
            color: '#CCCCCC',
            dashStyle: 'Solid',
            value: i,
            width: 1,
          },
        ];
      }
      grids = [
        ...grids,
        {
          color: bigBladderNotificationSetting ? '#FFBB00' : '#C0C0C0',
          dashStyle: 'Solid',
          value: (bladderThresholdPercentage ?? 0) / 10 - 0.03,
          width: 2,
          zIndex: 1,
        },
      ];
      return grids;
    }, [bigBladderNotificationSetting, bladderThresholdPercentage]);

    const options: Highcharts.Options = {
      chart: {
        type: 'area',
        zoomType: 'x',
        height: 148,
        marginBottom: 20,
      },
      time: {
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      title: {
        text: '',
      },
      xAxis: {
        type: 'datetime',
        min: Date.parse(dateRange.start),
        max: Date.parse(dateRange.end),
        showEmpty: true,
        opposite: false,
        gridLineWidth: 1,
        lineWidth: 0,
        tickWidth: 0,
        minPadding: 0,
        maxPadding: 0,
        plotLines: [
          {
            color: '#CCCCCC',
            dashStyle: 'Solid',
            value: Date.parse(dateRange.start),
            width: 0.5,
          },
          {
            color: '#CCCCCC',
            dashStyle: 'Solid',
            value: Date.parse(dateRange.end),
            width: 0.5,
          },
        ],
        dateTimeLabelFormats: {
          minute: '%H:%M',
          hour: '%H:00',
          day: t('graphDayFormat', '%m月%d日'),
          week: t('graphDayFormat', '%m月%d日'),
          month: t('graphYearFormat', '%m月%d日'),
        },
        crosshair: {
          width: 2,
        },
      },
      yAxis: {
        title: {
          text: '',
        },
        labels: {
          formatter: function () {
            return this.value as string;
          },
        },
        plotLines: getYGrids(),
        max: 10,
        min: 0,
        tickInterval: 2,
        crosshair: {
          width: 2,
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        area: {
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
            stops: [
              [0, 'rgba(123, 214, 231, 0.5)'],
              [1, 'rgba(123, 214, 231, 0.5)'],
            ],
          },
          lineWidth: 3,
          states: {
            hover: {
              lineWidth: 3,
            },
          },
          threshold: null,
        },
        series: {
          marker: {
            enabled: false,
          },
        },
      },
      series: [
        // @ts-ignore
        {
          name: t('urineGraphLabel', '尿量'),
          // @ts-ignore
          data: sensorReactions,
          color: '#7BD6E7',
          zIndex: 1,
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: t('graphDateTimeFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
          },
        },
        {
          name: t('rawGraph', '補正前グラフ'),
          // @ts-ignore
          data: rawSensorReactions ?? [],
          zIndex: 0,
          type: 'area',
          fillColor: {
            linearGradient: {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: 1,
            },
            stops: [
              [0, 'rgba(105, 147, 209, 1)'],
              [1, 'rgba(187, 212, 251, 0.2)'],
            ],
          },
          lineWidth: 1,
          lineColor: '#C0C0C0',
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: t('graphDateTimeFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
          },
          visible: showRawGraph,
        },
        {
          name: t('notification', '通知'),
          marker: {
            enabled: true,
            symbol: 'circle',
          },
          data: (sensorNotifications ?? [])
            .filter((notification) => {
              const { notificationType } = notification;
              return (
                notificationType === 'urinated' || notificationType === 'bigBladder' || notificationType === 'gotUp'
              );
            })
            .map((notification) => {
              const { notificationType, timestamp } = notification;
              let label = '';
              let color = '';
              if (notificationType === 'urinated') {
                label = t('checkGraphLabel', 'でたかも');
                color = 'rgba(255, 143, 165, 0.8)';
              } else if (notificationType === 'bigBladder') {
                label = t('almostGraphLabel', 'そろそろ');
                color = 'rgba(255, 187, 0, 0.8)';
              } else {
                label = t('gotUpGraphLabel', '起き上がり');
                color = 'rgba(255, 50, 35, 0.8)';
              }
              return {
                x: Date.parse(timestamp),
                y: 0,
                name: label,
                color: color,
              };
            }),
          type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
          lineWidth: 0,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
          },
          stickyTracking: false,
        },
        {
          name: t('careHistory', 'ケア履歴'),
          marker: {
            enabled: true,
            symbol: 'square',
          },
          zIndex: 3,
          data: (careRecordPlots || []).map((careRecordPlots) => {
            const { toilet, pad, timestamp } = careRecordPlots;
            const label = `${t('toilet', 'トイレ')}: ${toilet ? t('yes', '有') : t('no', '無')} ${t(
              'diaper',
              'おむつ/パッド',
            )}: ${pad ? t('yes', '有') : t('no', '無')}`;
            let color;
            if (toilet && !pad) color = '#72fbfd';
            if (toilet && pad) color = '#75f94c';
            if (!toilet && pad) color = '#CCCCCC';
            if (!toilet && !pad) color = '#6D6D6D';
            return {
              x: Date.parse(timestamp),
              y: 10,
              name: label,
              color,
            };
          }),
          type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
          lineWidth: 0,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          tooltip: {
            headerFormat: '<b>{series.name}</b><br>',
            pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
          },
        },
      ],
      credits: {
        enabled: false,
      },
    };

    const activityStatusData = React.useMemo(() => {
      const data = createActivityStatusHeatmapBar(activityStatusAnalysis);
      const filteredData = data.filter(([timestamp]) => timestamp < Date.parse(dateRange.end));
      if (filteredData.length) return filteredData;
      return [[Date.parse(dateRange.start), 0, null]] as [number, number, number | null][];
    }, [activityStatusAnalysis, dateRange.start, dateRange.end]);

    const activityTrendOptions: Highcharts.Options = {
      title: {
        text: '',
      },
      chart: {
        zoomType: 'x',
        height: 32,
        marginTop: 8,
        marginLeft: 40,
        marginRight: 10,
        marginBottom: 4,
        shadow: false,
        animation: false,
        backgroundColor: '#FFFFFF',
      },
      time: {
        timezoneOffset: new Date().getTimezoneOffset(),
      },
      xAxis: {
        type: 'datetime',
        min: Date.parse(dateRange.start),
        max: Date.parse(dateRange.end) - 5 * 60 * 1000, // 尿量の時間軸と合わせるために終端5分のデータを非表示
        minPadding: 0,
        maxPadding: 0,
        startOnTick: false,
        endOnTick: false,
        showEmpty: true,
        lineWidth: 0,
        tickWidth: 0,
        tickLength: 0,
        opposite: false,
        dateTimeLabelFormats: {
          minute: '%H:%M',
          hour: '%H:00',
          day: t('graphDayFormat', '%m月%d日'),
          week: t('graphDayFormat', '%m月%d日'),
          month: t('graphYearFormat', '%m月%d日'),
        },
        labels: {
          enabled: false,
        },
      },
      yAxis: {
        title: {
          text: i18n.language?.startsWith('ja') ? '姿勢' : '',
          style: {
            fontSize: '10px',
          },
          rotation: 0,
          x: -10,
          y: 4,
        },
        categories: [''],
        showEmpty: true,
        reversed: false,
        gridLineWidth: 0,
        plotLines: [
          {
            color: color.border_gray,
            width: 1,
            value: -0.5,
            zIndex: 2,
          },
          {
            color: color.border_gray,
            width: 1,
            value: 0.5,
            zIndex: 2,
          },
        ],
        labels: {
          enabled: false,
        },
      },
      colorAxis: {
        min: 0,
        max: 7,
        stops: [
          [0, '#FFF4CC'],
          [0.125, '#FFF4CC'],
          [0.25, '#000000'],
          [0.375, '#141F80'],
          [0.5, '#D7F5FF'],
          [0.625, '#5855DB'],
          [0.75, '#4EC1D0'],
          [0.875, '#B092E2'],
          [1, '#FF7152'],
        ],
      },
      series: [
        {
          type: 'heatmap',
          boostThreshold: 100,
          data: activityStatusData,
          colsize: 8.5 * 36e3,
          rowsize: 1,
          index: 0,
          turboThreshold: Number.MAX_VALUE,
          nullColor: 'rgba(0, 0, 0, 0)',
          dataLabels: { enabled: false },
        },
      ],
      tooltip: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      boost: {
        useGPUTranslations: true,
      },
    };

    const gasLevel = React.useMemo(() => {
      const baseURL = '/images/residents/meter/gas-level-';
      const value = resident.gasPercentage !== null && resident.gasPercentage;
      const isDisPlayGas = !!resident.gasSerialNumber && resident.gasPercentage !== null;
      const isDisplayUrine = !!resident.serialNumber && resident.urineSize !== null;

      return (
        <div css={GasLevelMeter(!isDisplayUrine && isDisPlayGas)}>
          {!resident.gasSerialNumber ? (
            <img src={`${baseURL}unregistered.svg`} alt='未登録' />
          ) : resident.gasPercentage === null ? (
            <>
              <img src={`${baseURL}disconnected.svg`} alt={t('notConnected', '未接続')} />
              <span css={BDFreeDisconnectedText}>{t('notConnected', '未接続')}</span>
            </>
          ) : (
            <img src={`${baseURL}${value}.svg`} alt={`たまり具合${value}`} />
          )}
        </div>
      );
    }, [resident.gasSerialNumber, resident.gasPercentage, resident.serialNumber, resident.urineSize]);

    const urineLevel = React.useMemo(() => {
      const baseURL = '/images/residents/meter/urine-level-';
      const color =
        resident.sensorErrorNotification || resident.zeroResetNotification || resident.upsideDownNotification
          ? 'navy'
          : 'blue';

      const value =
        resident.urineSize !== null &&
        (resident.urineVolumeResetFlg // グラフ0リセットフラグが立っている時は強制的に尿量を0にする
          ? 0
          : Math.round(resident.urineSize / 10));

      return (
        <div css={UrineLevelMeter}>
          {!resident.serialNumber ? (
            <img src={`${baseURL}unregistered.svg`} alt='未登録' />
          ) : resident.urineSize === null ? (
            <>
              <img src={`${baseURL}disconnected.svg`} alt={t('notConnected', '未接続')} />
              <span css={DFreeDisconnectedText}>{t('notConnected', '未接続')}</span>
            </>
          ) : (
            <img src={`${baseURL}${color}-${value}.svg`} alt={`たまり具合${value}`} />
          )}
        </div>
      );
    }, [
      resident.serialNumber,
      resident.urineSize,
      resident.sensorErrorNotification,
      resident.zeroResetNotification,
      resident.upsideDownNotification,
      resident.urineVolumeResetFlg,
    ]);

    const urineBar = React.useMemo(() => {
      const baseURL = '/images/residents/meter/urine-bar-';
      if (!resident.serialNumber) {
        return null;
      }
      if (typeof resident.bladderThresholdPercentage === 'number') {
        const value = Math.round(resident.bladderThresholdPercentage / 10);
        return (
          <div css={UrineThreshold((5 - value) * 11)}>
            <img src={`${baseURL}${value}.svg`} alt={`そろそろライン${value}`} />
          </div>
        );
      }
      return null;
    }, [resident.serialNumber, resident.bladderThresholdPercentage]);

    const formattedGasPredictionTime = React.useMemo(() => {
      if (resident.gasPredictionTime) {
        const durationObj = dayjs.duration(resident.gasPredictionTime, 'minutes');
        const days = Math.floor(durationObj.asDays());
        const hours = durationObj.hours();
        const mins = durationObj.minutes();

        if (days >= 1) {
          const separator = i18n.language.startsWith('ja') ? '' : ' ';
          return `${days}${t('d', '日')}${hours !== 0 ? `${separator}${hours}${t('Hr', '時間')}` : ''}`;
        } else if (hours >= 1) {
          return `${hours}${t('Hr', '時間')}${mins !== 0 ? `${mins}${t('Min', '分')}` : ''}`;
        }
        return `${mins}${t('Min', '分')}`;
      }
      return null;
    }, [resident.gasPredictionTime, i18n.language, t]);

    const gasDaysOfNoDefecation = React.useMemo(() => {
      // 更新中
      if (resident.existsCurrentPoopedRecord) {
        return (
          <div css={DotAnimeContainer}>
            <div css={ThreeDotsAnimation}></div>
          </div>
        );
      }
      // 更新なし
      return resident.gasDaysOfNoDefecation !== null ? (
        <span css={GasDaysOfNoDefecationValue(color.white, color.notification_orange)}>
          {resident.gasDaysOfNoDefecation === 0 ? 0 : `-${resident.gasDaysOfNoDefecation}`}
        </span>
      ) : (
        <span css={GasDaysOfNoDefecationValue(color.dark_gray, 'transparent')}>-</span>
      );
    }, [resident.existsCurrentPoopedRecord, resident.gasDaysOfNoDefecation]);

    return (
      <>
        <ResidentItemContainer resident={resident}>
          <DfreeGraphListItemHeader resident={resident} handleClickRecord={handleClickRecord} />
          <div css={ResidentContent}>
            <div css={LeftContainer} data-testid='meter'>
              {gasLevel}
              {urineLevel}
              {urineBar}
            </div>
            <div css={GraphContainer}>
              <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
              <HighchartsReact highcharts={Highcharts} options={activityTrendOptions} />
              <ActivityTrendColorGuideTooltip />
            </div>
            <div css={RightContainer}>
              <div css={GasDaysOfNoDefecation} data-testid='days-of-no-defecation'>
                {t('numberOfDaysWithoutBowelMovements', '便通がない日数')}
                {gasDaysOfNoDefecation}
              </div>
              <div css={CareGuideline}>
                <div css={GasPrediction}>
                  <div css={GasPredictionInner} data-testid='gas-prediction-time'>
                    <div css={GasPredictionTitle}>{t('estimatedTimeOfDefecation', '排便予測時間')}</div>
                    {resident.gasPredictionTime ? (
                      <div css={GasPreditionTime(color.notification_orange)}>{formattedGasPredictionTime}</div>
                    ) : (
                      <div css={GasPreditionTime(color.dark_gray)}>-</div>
                    )}
                    <HelpTooltip
                      text={t(
                        'averageTimeFromAdministrationOfStimulantLaxativeToDefecation(calculatedUsingInformationFromThePast30Days)',
                        '刺激性下剤投与から排便までの平均時間。（過去30日の情報で算出）',
                      )}
                    />
                  </div>
                </div>
                <div css={LaxativeTime}>
                  <div>{t('laxativeElapsedTime', '下剤経過時間')}</div>
                  {resident.existsCurrentLaxativeRecord ? (
                    <div css={DotAnimeContainer}>
                      <div css={ThreeDotsAnimation}></div>
                    </div>
                  ) : (
                    <div css={LaxativeTimeMeter}>
                      {resident.gasElapsedTime !== null && (
                        <div css={LaxativeTimeMeterGasElapsedTime(resident.gasElapsedTime)}></div>
                      )}
                      <div css={LaxativeTimeMeterGuideline(6)}>
                        <span>6h</span>
                      </div>
                      <div css={LaxativeTimeMeterGuideline(12)}>
                        <span>12h</span>
                      </div>
                      <div css={LaxativeTimeMeterGuideline(18)}>
                        <span>18h</span>
                      </div>
                      <div css={LaxativeTimeMeterGuideline(24)}>
                        <span>24h</span>
                      </div>
                      {resident.gasPredictionTime !== null && (
                        <div css={LaxativeTimeMeterGasPreditionTime(resident.gasPredictionTime)}></div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 通知カバーがあるときはグラフの下に通知コンテンツを表示 */}
          <NotificationCover resident={resident} config={notificationCoverConfig} />
        </ResidentItemContainer>
      </>
    );
  }
);

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: color.dark_gray,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 192,
    backgroundColor: color.dark_gray,
    borderRadius: 10,
    fontFamily:
      "'Noto Sans JP', 'ヒラギノ角ゴ Pro W3', 'Hiragino Kaku Gothic Pro', 'メイリオ', Meiryo, Osaka, 'ＭＳ Ｐゴシック', 'MS PGothic', sans-serif",
    fontWeight: 'normal !important',
    fontSize: '12px',
    lineHeight: '16px',
    padding: '8px 12px',
    boxShadow: '0px 4px 4px 0px #00000040',
  },
}));

const HelpTooltip = ({ text }: { text: string }) => (
  <CustomTooltip
    sx={{
      padding: 0,
    }}
    title={
      <div>
        <div>{text}</div>
      </div>
    }
    placement='top-end'
    PopperProps={{
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [20, -5], // オフセットをカスタマイズ (X, Y)
          },
        },
      ],
    }}
  >
    <IconButton sx={{ padding: 0 }}>
      <img src='/images/icons/question.svg' alt='ヘルプ' />
    </IconButton>
  </CustomTooltip>
);
